import { observer, useLocalStore } from 'mobx-react-lite';
// eslint-disable-next-line import/default
import moment from 'moment';
import * as React from 'react';
import Datepicker from 'react-datepicker';
import BeatLoader from 'react-spinners/BeatLoader';

import 'react-datepicker/dist/react-datepicker.css';
import './calculator-form.css';

import calendar from '../assets/calendar.svg';
import mastercard from '../assets/mastercard.svg';
import visaBlack from '../assets/visa-black.svg';
import visa from '../assets/visa.svg';
import { AmountInput } from '../components/amount-input';
import { ButtonSwitch, IOption } from '../components/button-switch';
import { InputGroup } from '../components/input-group';
import { CardType } from '../contracts';
import { Store } from '../stores/store';

interface IProps {
    store: Store;
}

export const CalculatorForm = observer<IProps>(({ store }) => {
    const localStore = useLocalStore(() => ({
        get minDate() {
            return store.cardType === CardType.Visa ? moment().add(-3, 'months').toDate() : moment('05-01-2020').toDate();
        },
        maxDate: moment().add(-1, 'day').toDate(),
        onSubmit: async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            await store.calculateFees();
        },
        onSwitch: (e: React.MouseEvent) => {
            e.preventDefault();
            store.switchAmounts();
        },
    }));

    return (
        <div className='calculator-wrapper'>
            <form onSubmit={localStore.onSubmit}>
                <InputGroup label='Select your card' info=''>
                    <ButtonSwitch value={store.cardType} options={CardOptions(store.cardType)} onChange={store.setCardType} />
                </InputGroup>
                <InputGroup label='You paid' info='This is the original amount from the receipt or the invoice paid in the foreign currency.'>
                    <AmountInput amount={store.paidAmount} onAmountChanged={store.setPaidAmount} required />
                </InputGroup>
                <InputGroup label='Your card got charged' info='This is the amount shown on your card statement for this transaction. It is in the currency of your corporate card.'>
                    <AmountInput amount={store.bankChargeAmount} onAmountChanged={store.setBankCharge} required />
                </InputGroup>
                <InputGroup label='Additional charges (optional)' info='These are additional fees that your bank charged you for this transaction. These could be ATM fees, POS fees, etc...'>
                    <AmountInput amount={store.additionalChargeAmount} onAmountChanged={store.setAdditionalCharge} readonly />
                </InputGroup>
                <InputGroup label='You paid on'>
                    <div className='date-container'>
                        <img alt='calendar' src={calendar} />
                        <Datepicker
                            selected={store.paymentDate}
                            onChange={store.setDate}
                            dateFormat='dd MMM yyyy'
                            minDate={localStore.minDate}
                            maxDate={localStore.maxDate}
                            required
                            className='date-input'
                            placeholderText='Choose date'

                            popperPlacement='top'
                            popperModifiers={{
                                flip: {
                                    behavior: ['top', 'left'], // don't allow it to flip to be above
                                },
                                preventOverflow: {
                                    enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
                                },
                                hide: {
                                    enabled: false, // turn off since needs preventOverflow to be enabled
                                },
                            }}
                        />
                    </div>
                </InputGroup>
                <div>
                    <button disabled={store.isCalculating} type='submit' className='btn btn-green'>{store.isCalculating ? <BeatLoader color='white' /> : 'Calculate'}</button>
                </div>
            </form>
        </div>
    );
});

const CardOptions = (currentCardType: CardType) => {
    const options: { [key: string]: IOption } = {
        [CardType.Visa]: {
            value: CardType.Visa,
            display: <img alt='visa' src={currentCardType === CardType.Visa ? visa : visaBlack} />,
            disabled: false,
        },
        [CardType.Mastercard]: {
            value: CardType.Mastercard,
            display:
                <img alt='mastercard' src={mastercard} />,
            disabled: false,
        },
    };

    return options;
};

// You paid: This is the original amount from the receipt or the invoice paid in the foreign currency.
// Your bank statement shows: This is the amount shown on your card statement for this transaction. It is in the currency of your corporate card.
// Additional related charges: These are additional fees that your bank charged you for this transaction. These could be ATM fees, POS fees, etc... (edited)